import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import Badge from '@material-ui/core/Badge';
import Tooltip from '@material-ui/core/Tooltip';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import StarIcon from '@material-ui/icons/Star';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import { getNewReleases, getVolumePublishers, getSeriesById } from './comicDataProvider';
import { getLastComicDate, getTrackedVolumeInfo } from './services/releasesService';
import { updateComicOwnership } from './services/comicsService';
import { addComicToTrackedSeries } from './services/seriesService';
import { getBadgeContent, getImageUrl, hasValidImage, isComicOwned } from './utils';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
    maxWidth: 1400,
    margin: '0 auto',
  },
  pageTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(4),
  },
  errorContainer: {
    padding: theme.spacing(2),
    color: theme.palette.error.main,
    textAlign: 'center',
  },
  emptyContainer: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: '#666',
  },
  filterBar: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#fff',
    border: '2px solid #1976d2',
    borderRadius: 6,
  },
  controlGroup: {
    border: '1px solid #e3ecfb',
    borderRadius: 6,
    padding: theme.spacing(2),
    backgroundColor: '#f8fbff',
  },
  controlGroupTitle: {
    display: 'inline-block',
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#1976d2',
    marginBottom: theme.spacing(1.25),
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
    lineHeight: 1.2,
  },
  controlRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: theme.spacing(1),
  },
  publisherControl: {
    minWidth: 0,
    width: '100%',
  },
  toggleGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  toggleLabel: {
    margin: 0,
    '& .MuiFormControlLabel-label': {
      whiteSpace: 'normal',
      lineHeight: 1.2,
    },
  },
  runRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginTop: theme.spacing(1),
  },
  runButton: {
    marginLeft: 0,
  },
  sectionContainer: {
    marginBottom: theme.spacing(2),
    border: '1px solid #ddd',
    borderRadius: 4,
    overflow: 'hidden',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    backgroundColor: '#f5f5f5',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: '#ebebeb',
    },
  },
  sectionTitle: {
    fontWeight: 600,
    flexGrow: 1,
    fontSize: '1rem',
  },
  sectionCount: {
    fontSize: '0.85rem',
    color: '#555',
    marginRight: theme.spacing(1),
  },
  issueGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(1),
    gap: theme.spacing(0.5),
  },
  issueWrapper: {
    position: 'relative',
    width: 100,
    textAlign: 'center',
    padding: theme.spacing(0.5),
  },
  issueImage: {
    width: 100,
    height: 150,
    objectFit: 'cover',
    display: 'block',
    borderRadius: 2,
  },
  issuePlaceholder: {
    width: 100,
    height: 150,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0f0f0',
    border: '1px solid #ccc',
    textAlign: 'center',
    padding: theme.spacing(1),
    boxSizing: 'border-box',
    fontSize: '0.7em',
    wordWrap: 'break-word',
    overflow: 'hidden',
    borderRadius: 2,
  },
  issueTitle: {
    width: 100,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '0.72em',
    marginTop: 2,
  },
  issueDates: {
    marginTop: 2,
    fontSize: '0.63em',
    color: '#555',
    lineHeight: 1.25,
    textAlign: 'left',
    width: 100,
  },
  // Gold border for issues in a tracked series
  tracked: {
    outline: '3px solid #FFD700',
    outlineOffset: 1,
    borderRadius: 2,
  },
  // Green badge overlay for owned issues
  ownedOverlay: {
    position: 'absolute',
    top: 4,
    right: 4,
    color: '#4caf50',
    backgroundColor: 'rgba(255,255,255,0.85)',
    borderRadius: '50%',
    display: 'flex',
  },
  trackedOverlay: {
    position: 'absolute',
    top: 4,
    left: 4,
    color: '#FFD700',
    backgroundColor: 'rgba(0,0,0,0.55)',
    borderRadius: '50%',
    display: 'flex',
  },
  legend: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    backgroundColor: '#fafafa',
    border: '1px solid #eee',
    borderRadius: 4,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: '0.85rem',
  },
}));

const DATE_MODES = {
  ADDED: 'added',
  COVER: 'cover',
  STORE: 'store',
  AUTO: 'auto',
};

const RANGE_MODES = {
  LAST_4_WEEKS: 'last4w',
  LAST_8_WEEKS: 'last8w',
  NEXT_1_MONTH: 'next1m',
  NEXT_2_MONTHS: 'next2m',
  THIS_MONTH: 'thisMonth',
  LAST_MONTH: 'lastMonth',
  SINCE_LAST_OWNED: 'sinceLastOwned',
};

const GROUP_MODES = {
  WEEK: 'week',
  MONTH: 'month',
};

const DEFAULT_PUBLISHERS = ['Marvel', 'DC Comics'];

function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDateValue(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function startOfWeek(date) {
  const d = startOfDay(date);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

function endOfWeek(date) {
  const d = startOfWeek(date);
  d.setDate(d.getDate() + 6);
  return endOfDay(d);
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
  return endOfDay(new Date(date.getFullYear(), date.getMonth() + 1, 0));
}

function addMonths(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

function parseIssueDateValue(issue, dateMode) {
  const candidatesByMode = {
    [DATE_MODES.ADDED]: [issue.dateAdded, issue.date_added],
    [DATE_MODES.COVER]: [issue.coverDate, issue.cover_date],
    [DATE_MODES.STORE]: [issue.storeDate, issue.store_date],
    [DATE_MODES.AUTO]: [
      issue.dateAdded,
      issue.date_added,
      issue.coverDate,
      issue.cover_date,
      issue.storeDate,
      issue.store_date,
    ],
  };

  const candidates = candidatesByMode[dateMode] || candidatesByMode[DATE_MODES.AUTO];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const parsed = new Date(candidate);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  return null;
}

function weekLabel(startDate) {
  const end = new Date(startDate);
  end.setDate(end.getDate() + 6);
  const fmt = d => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return `Week of ${fmt(startDate)} – ${fmt(end)}`;
}

function monthLabel(startDate) {
  return startDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function buildSections(startDate, endDate, grouping) {
  const sections = [];
  if (grouping === GROUP_MODES.MONTH) {
    const cursor = startOfMonth(startDate);
    while (cursor <= endDate) {
      const sectionStart = new Date(cursor);
      const sectionEnd = endOfMonth(cursor);
      sections.push({
        key: `${sectionStart.getFullYear()}-${String(sectionStart.getMonth() + 1).padStart(2, '0')}`,
        label: monthLabel(sectionStart),
        startDate: sectionStart,
        endDate: sectionEnd,
      });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return sections;
  }

  const cursor = startOfWeek(startDate);
  const final = endOfWeek(endDate);
  while (cursor <= final) {
    const sectionStart = new Date(cursor);
    const sectionEnd = endOfWeek(cursor);
    sections.push({
      key: toYMD(sectionStart),
      label: weekLabel(sectionStart),
      startDate: sectionStart,
      endDate: sectionEnd,
    });
    cursor.setDate(cursor.getDate() + 7);
  }
  return sections;
}

// Infer a publisher label for an issue.
// Tracked series have a known publisher from Firestore (publisherMap).
// For untracked series, fall back to well-known name patterns.
const PUBLISHER_PATTERNS = [
  { pattern: /\bmarvel\b/i, name: 'Marvel' },
  { pattern: /\b(?:dc\s+comics|dc)\b/i, name: 'DC Comics' },
  { pattern: /\bimage\b/i, name: 'Image Comics' },
  { pattern: /\bdark\s+horse\b/i, name: 'Dark Horse' },
  { pattern: /\bidw\b/i, name: 'IDW' },
  { pattern: /\bvaliant\b/i, name: 'Valiant' },
  { pattern: /\bdynamite\b/i, name: 'Dynamite' },
  { pattern: /\bahoy\b/i, name: 'AHOY Comics' },
  { pattern: /\bboom\b/i, name: 'BOOM! Studios' },
];

function getIssuePublisher(issue, publisherMap) {
  const volumeId = issue.series && issue.series.id ? Number(issue.series.id) : null;
  if (volumeId !== null && publisherMap.has(volumeId)) {
    return publisherMap.get(volumeId);
  }
  const seriesName = (issue.series && issue.series.name) || '';
  for (const { pattern, name } of PUBLISHER_PATTERNS) {
    if (pattern.test(seriesName)) return name;
  }
  return 'Other';
}

const NewReleases = ({ appuser, comicsIGot, cards, addComics, updateIGotStatus, cache, onCacheUpdate }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sections, setSections] = useState(cache ? cache.sections : []); // [{ label, startDate, issues: [] }]
  const [expandedSections, setExpandedSections] = useState(cache ? cache.expandedSections : {});
  const [trackedVolumeIds, setTrackedVolumeIds] = useState(cache ? cache.trackedVolumeIds : new Set());
  const [volumePublisherMap, setVolumePublisherMap] = useState(cache ? cache.volumePublisherMap : new Map());
  // Also track which hero owns each tracked volume (volumeId -> heroName)
  const [volumeHeroMap, setVolumeHeroMap] = useState(cache ? cache.volumeHeroMap : new Map());
  // Map volumeId -> series title as stored in Firebase
  const [volumeSeriesTitleMap, setVolumeSeriesTitleMap] = useState(cache ? cache.volumeSeriesTitleMap : new Map());

  // Filter state
  const [selectedPublishers, setSelectedPublishers] = useState(cache ? cache.selectedPublishers : DEFAULT_PUBLISHERS);
  const [mySeriesOnly, setMySeriesOnly] = useState(cache ? cache.mySeriesOnly : false);
  const [dateMode, setDateMode] = useState(cache ? cache.dateMode : DATE_MODES.STORE);
  const [rangeMode, setRangeMode] = useState(cache ? cache.rangeMode : RANGE_MODES.SINCE_LAST_OWNED);
  const [groupMode, setGroupMode] = useState(cache ? cache.groupMode : GROUP_MODES.WEEK);
  const [hideBeforeLastRecorded, setHideBeforeLastRecorded] = useState(cache ? cache.hideBeforeLastRecorded : false);
  const [queryRequest, setQueryRequest] = useState(cache ? cache.queryRequest : null);

  // Multiselect state
  const [selectedIssues, setSelectedIssues] = useState(new Set()); // Set of issue ids
  const [adding, setAdding] = useState(false);
  // Hero selection dialog for untracked series
  const [heroDialog, setHeroDialog] = useState({ open: false, seriesName: '', volumeId: null, pendingIssues: [] });

  const userEmail = appuser && appuser.email;
  const [lastOwnedDate, setLastOwnedDate] = useState(null);

  useEffect(() => {
    if (!userEmail) return undefined;
    let cancelled = false;
    getLastComicDate(userEmail)
      .then(d => { if (!cancelled) setLastOwnedDate(d || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [userEmail]);

  const load = useCallback(async (request) => {
    if (!userEmail) return;
    if (!request) return;
    setLoading(true);
    setError(null);

    try {
      // 1. Load tracked volume IDs and publisher map for matching/filtering
      const { volumeIds, publisherMap, heroMap, seriesTitleMap } = await getTrackedVolumeInfo(userEmail);
      setTrackedVolumeIds(volumeIds);
      setVolumePublisherMap(publisherMap);
      setVolumeHeroMap(heroMap);
      setVolumeSeriesTitleMap(seriesTitleMap);

      // 2. Determine date range and grouping.
      const now = new Date();
      const today = endOfDay(now);
      let startDate = startOfDay(now);
      let endDate = new Date(today);
      const shouldLoadLastRecordedDate = request.rangeMode === RANGE_MODES.SINCE_LAST_OWNED || request.hideBeforeLastRecorded;
      const lastRecordedDate = shouldLoadLastRecordedDate ? await getLastComicDate(userEmail) : null;

      if (request.rangeMode === RANGE_MODES.LAST_4_WEEKS) {
        startDate.setDate(startDate.getDate() - 28);
      } else if (request.rangeMode === RANGE_MODES.LAST_8_WEEKS) {
        startDate.setDate(startDate.getDate() - 56);
      } else if (request.rangeMode === RANGE_MODES.NEXT_1_MONTH) {
        startDate.setDate(startDate.getDate() + 1);
        endDate = endOfDay(addMonths(startDate, 1));
      } else if (request.rangeMode === RANGE_MODES.NEXT_2_MONTHS) {
        startDate.setDate(startDate.getDate() + 1);
        endDate = endOfDay(addMonths(startDate, 2));
      } else if (request.rangeMode === RANGE_MODES.THIS_MONTH) {
        startDate = startOfMonth(today);
        endDate = endOfMonth(today);
      } else if (request.rangeMode === RANGE_MODES.LAST_MONTH) {
        startDate = startOfMonth(addMonths(today, -1));
        endDate = endOfMonth(addMonths(today, -1));
      } else if (request.rangeMode === RANGE_MODES.SINCE_LAST_OWNED) {
        const fallbackLookback = startOfDay(new Date(today));
        fallbackLookback.setDate(fallbackLookback.getDate() - 56);
        if (!lastRecordedDate) {
          startDate = fallbackLookback;
        } else if (lastRecordedDate > today) {
          // getLastComicDate can return future only when no past-dated entries exist.
          startDate = fallbackLookback;
        } else {
          startDate = startOfDay(lastRecordedDate);
        }
      }

      const sectionRanges = buildSections(startDate, endDate, request.groupMode);

      // 3. Fetch all issues for the full range in one request (paginated if needed)
      const startStr = toYMD(sectionRanges[0].startDate);
      const endStr = toYMD(sectionRanges[sectionRanges.length - 1].endDate);

      const fetchAllIssues = async (dateField) => {
        let issues = [];
        let offset = 0;
        let hasMore = true;
        while (hasMore) {
          const response = await getNewReleases(startStr, endStr, 100, offset, { dateField });
          issues = issues.concat(response.results || []);
          hasMore = response.hasMore;
          offset += 100;
          // Safety: cap at 500 issues to respect rate limits
          if (offset >= 500) break;
        }
        return issues;
      };

      let allIssues = [];
      if (request.dateMode === DATE_MODES.COVER) {
        allIssues = await fetchAllIssues('cover');
      } else if (request.dateMode === DATE_MODES.STORE) {
        allIssues = await fetchAllIssues('store');
      } else if (request.dateMode === DATE_MODES.ADDED) {
        allIssues = await fetchAllIssues('added');
      } else {
        // AUTO mode queries both and merges, so missing date_added records still appear.
        const [addedIssues, coverIssues] = await Promise.all([
          fetchAllIssues('added'),
          fetchAllIssues('cover'),
        ]);

        const deduped = new Map();
        [...addedIssues, ...coverIssues].forEach((issue) => {
          const key = issue.comicVineId || issue.id;
          if (!deduped.has(key)) {
            deduped.set(key, issue);
          }
        });
        allIssues = Array.from(deduped.values());
      }

      // 4a. Fetch publisher info for untracked volumes from Comic Vine
      const untrackedVolumeIds = [];
      const seenVolumes = new Set();
      allIssues.forEach(issue => {
        const vid = issue.series && issue.series.id ? Number(issue.series.id) : null;
        if (vid && !volumeIds.has(vid) && !seenVolumes.has(vid)) {
          seenVolumes.add(vid);
          untrackedVolumeIds.push(vid);
        }
      });
      if (untrackedVolumeIds.length > 0) {
        const apiPublishers = await getVolumePublishers(untrackedVolumeIds);
        const merged = new Map(publisherMap);
        apiPublishers.forEach((pub, vid) => merged.set(vid, pub));
        setVolumePublisherMap(merged);
      }

      // 4b. Group issues by selected section (week/month)
      const hideBeforeDate = request.hideBeforeLastRecorded && lastRecordedDate
        ? startOfDay(lastRecordedDate)
        : null;
      const sectionData = sectionRanges.map((range) => {
        const issues = allIssues.filter(issue => {
          const storeDate = parseIssueDateValue(issue, DATE_MODES.STORE);
          if (!storeDate) return false;
          if (hideBeforeDate && storeDate < hideBeforeDate) return false;
          return storeDate >= range.startDate && storeDate <= range.endDate;
        });
        return {
          key: range.key,
          label: range.label,
          startDate: range.startDate,
          issues,
        };
      });

      // Remove empty sections
      const nonEmpty = sectionData.filter(section => section.issues.length > 0);
      setSections(nonEmpty);

      // Auto-expand the most recent section
      if (nonEmpty.length > 0) {
        setExpandedSections({ [nonEmpty[0].key]: true });
      }
    } catch (err) {
      console.error('Error loading new releases:', err);
      setError(`Failed to load new releases: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    if (queryRequest) {
      load(queryRequest);
    }
  }, [load, queryRequest]);

  // Persist state to parent so results survive navigation
  useEffect(() => {
    if (onCacheUpdate) {
      onCacheUpdate({
        sections, expandedSections, trackedVolumeIds, volumePublisherMap, volumeHeroMap, volumeSeriesTitleMap,
        selectedPublishers, mySeriesOnly, dateMode, rangeMode, groupMode, hideBeforeLastRecorded, queryRequest,
      });
    }
  }, [sections, expandedSections, trackedVolumeIds, volumePublisherMap, volumeHeroMap, volumeSeriesTitleMap,
    selectedPublishers, mySeriesOnly, dateMode, rangeMode, groupMode, hideBeforeLastRecorded, queryRequest, onCacheUpdate]);

  const toggleIssueSelection = (issueId) => {
    setSelectedIssues(prev => {
      const next = new Set(prev);
      if (next.has(issueId)) next.delete(issueId);
      else next.add(issueId);
      return next;
    });
  };

  // Build a flat lookup of all issues by id
  const issueById = useMemo(() => {
    const map = new Map();
    sections.forEach(s => s.issues.forEach(i => map.set(i.id, i)));
    return map;
  }, [sections]);

  // Handle "Add Selected" — group selected issues by volume, process each
  const handleAddSelected = useCallback(async () => {
    if (selectedIssues.size === 0 || !userEmail) return;
    setAdding(true);

    try {
      // Group selected issues by volume
      const byVolume = new Map(); // volumeId -> { issues: [], seriesName, volumeId }
      selectedIssues.forEach(issueId => {
        const issue = issueById.get(issueId);
        if (!issue) return;
        const vid = issue.series?.id ? Number(issue.series.id) : null;
        if (!vid) return;
        if (!byVolume.has(vid)) {
          byVolume.set(vid, { volumeId: vid, seriesName: issue.series.name, issues: [] });
        }
        byVolume.get(vid).issues.push(issue);
      });

      // Process tracked volumes first — mark as owned and add to series
      const untrackedVolumes = [];
      for (const [vid, group] of byVolume.entries()) {
        if (trackedVolumeIds.has(vid)) {
          const heroName = volumeHeroMap.get(vid);
          const seriesTitle = volumeSeriesTitleMap.get(vid);
          for (const issue of group.issues) {
            await updateComicOwnership(issue, true, userEmail, vid, heroName || 0);
            if (heroName && seriesTitle) {
              await addComicToTrackedSeries(issue, heroName, seriesTitle, userEmail);
            }
          }
        } else {
          untrackedVolumes.push(group);
        }
      }

      // For untracked volumes, prompt hero selection one at a time
      if (untrackedVolumes.length > 0) {
        // Process sequentially via dialog
        processUntrackedQueue(untrackedVolumes);
      } else {
        setSelectedIssues(new Set());
      }
    } catch (err) {
      console.error('Error adding selected issues:', err);
      alert(`Error adding issues: ${err.message || err}`);
    } finally {
      setAdding(false);
    }
  }, [selectedIssues, issueById, trackedVolumeIds, volumeHeroMap, volumeSeriesTitleMap, userEmail]);

  // Queue-based processing for untracked volumes
  const [untrackedQueue, setUntrackedQueue] = useState([]);

  const processUntrackedQueue = (queue) => {
    if (queue.length === 0) {
      setSelectedIssues(new Set());
      return;
    }
    const next = queue[0];
    setUntrackedQueue(queue.slice(1));
    setHeroDialog({ open: true, seriesName: next.seriesName, volumeId: next.volumeId, pendingIssues: next.issues });
  };

  const handleHeroSelected = useCallback(async (hero) => {
    setHeroDialog(prev => ({ ...prev, open: false }));
    const { volumeId, pendingIssues } = heroDialog;

    try {
      setAdding(true);
      // Fetch the full series from Comic Vine
      const series = await getSeriesById(volumeId);
      if (series) {
        // Add series under the selected hero (fetches all comics and saves to Firebase)
        await addComics(series, hero);
        // Now mark each selected issue as owned
        for (const issue of pendingIssues) {
          await updateComicOwnership(issue, true, userEmail, volumeId, hero.id || hero.name || 0);
        }
        // Update tracked state so subsequent selections know this volume is now tracked
        setTrackedVolumeIds(prev => {
          const next = new Set(prev);
          next.add(volumeId);
          return next;
        });
        setVolumeHeroMap(prev => {
          const next = new Map(prev);
          next.set(volumeId, hero.name);
          return next;
        });
      } else {
        alert(`Could not find series for volume ID ${volumeId}`);
      }
    } catch (err) {
      console.error('Error adding untracked series:', err);
      alert(`Error adding series: ${err.message || err}`);
    } finally {
      setAdding(false);
      // Process remaining queue
      processUntrackedQueue(untrackedQueue);
    }
  }, [heroDialog, untrackedQueue, userEmail, addComics]);

  const handleHeroDialogClose = () => {
    setHeroDialog(prev => ({ ...prev, open: false }));
    // Skip this volume and process remaining
    processUntrackedQueue(untrackedQueue);
  };

  // Derive all publishers present across all fetched issues
  const allPublishers = useMemo(() => {
    const pubSet = new Set();
    sections.forEach(section => {
      section.issues.forEach(issue => {
        const pub = getIssuePublisher(issue, volumePublisherMap);
        pubSet.add(pub);
      });
    });
    return Array.from(pubSet).sort();
  }, [sections, volumePublisherMap]);

  // Apply filters to sections
  const filteredSections = useMemo(() => {
    return sections.map(section => ({
      ...section,
      issues: section.issues.filter(issue => {
        const volumeId = issue.series && issue.series.id ? Number(issue.series.id) : null;
        const isTracked = volumeId !== null && trackedVolumeIds.has(volumeId);

        if (mySeriesOnly && !isTracked) return false;

        if (selectedPublishers.length > 0) {
          const pub = getIssuePublisher(issue, volumePublisherMap);
          if (!selectedPublishers.includes(pub)) return false;
        }

        return true;
      }),
    })).filter(section => section.issues.length > 0);
  }, [sections, mySeriesOnly, selectedPublishers, trackedVolumeIds, volumePublisherMap]);

  // Flat list sorted by publisher, then series name (alpha), then date
  const sortedIssues = useMemo(() => {
    const dateOf = (issue) => {
      const d = parseIssueDateValue(issue, DATE_MODES.STORE);
      return d ? d.getTime() : 0;
    };
    const items = [];
    filteredSections.forEach(section => section.issues.forEach(issue => {
      items.push({
        issue,
        pub: getIssuePublisher(issue, volumePublisherMap) || '',
        name: (issue.series && issue.series.name) || issue.title || '',
        date: dateOf(issue),
      });
    }));
    items.sort((a, b) =>
      a.pub.localeCompare(b.pub) ||
      a.name.localeCompare(b.name) ||
      a.date - b.date);
    return items.map(i => i.issue);
  }, [filteredSections, volumePublisherMap]);

  // Build the filter bar — shown regardless of whether any results loaded
  const filterBar = (
    <div className={classes.filterBar}>
      <div className={classes.controlGroup}>
        <div className={classes.controlGroupTitle}>Query parameters</div>
        <div className={classes.controlRow}>
          <TextField
            select
            variant="outlined"
            size="small"
            fullWidth
            label="Date basis"
            value={dateMode}
            onChange={e => setDateMode(e.target.value)}
          >
            <MenuItem value={DATE_MODES.ADDED}>Date added (Comic Vine)</MenuItem>
            <MenuItem value={DATE_MODES.COVER}>Cover date</MenuItem>
            <MenuItem value={DATE_MODES.STORE}>Store date</MenuItem>
            <MenuItem value={DATE_MODES.AUTO}>Date added then cover fallback</MenuItem>
          </TextField>

          <TextField
            select
            variant="outlined"
            size="small"
            fullWidth
            label="Range"
            value={rangeMode}
            onChange={e => setRangeMode(e.target.value)}
          >
            <MenuItem value={RANGE_MODES.LAST_4_WEEKS}>Last 4 weeks</MenuItem>
            <MenuItem value={RANGE_MODES.LAST_8_WEEKS}>Last 8 weeks</MenuItem>
            <MenuItem value={RANGE_MODES.NEXT_1_MONTH}>Next 1 month</MenuItem>
            <MenuItem value={RANGE_MODES.NEXT_2_MONTHS}>Next 2 months</MenuItem>
            <MenuItem value={RANGE_MODES.THIS_MONTH}>This month</MenuItem>
            <MenuItem value={RANGE_MODES.LAST_MONTH}>Last month</MenuItem>
            <MenuItem value={RANGE_MODES.SINCE_LAST_OWNED}>Since last owned date{lastOwnedDate ? ` (${formatDateValue(lastOwnedDate)})` : ''}</MenuItem>
          </TextField>

          <TextField
            select
            variant="outlined"
            size="small"
            fullWidth
            label="Group by"
            value={groupMode}
            onChange={e => setGroupMode(e.target.value)}
          >
            <MenuItem value={GROUP_MODES.WEEK}>Week</MenuItem>
            <MenuItem value={GROUP_MODES.MONTH}>Month</MenuItem>
          </TextField>
        </div>
        <div className={classes.runRow}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            className={classes.runButton}
            onClick={() => {
              setQueryRequest({ dateMode, rangeMode, groupMode, hideBeforeLastRecorded, runAt: Date.now() });
            }}
            disabled={!userEmail || loading}
          >
            Run query
          </Button>
        </div>
      </div>

      <div className={classes.controlGroup}>
        <div className={classes.controlGroupTitle}>Filters</div>
        <div className={classes.controlRow}>
          <TextField
            select
            variant="outlined"
            size="small"
            fullWidth
            className={classes.publisherControl}
            label="Publisher"
            InputLabelProps={{ shrink: true }}
            value={selectedPublishers}
            onChange={e => setSelectedPublishers(e.target.value)}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              renderValue: selected =>
                selected.length === 0
                  ? 'All publishers'
                  : `${selected.length} selected`,
            }}
          >
            {allPublishers.map(pub => (
              <MenuItem key={pub} value={pub}>
                <Checkbox checked={selectedPublishers.includes(pub)} size="small" />
                <ListItemText primary={pub} />
              </MenuItem>
            ))}
          </TextField>
        </div>

        <div className={classes.toggleGroup}>
          <FormControlLabel
            className={classes.toggleLabel}
            control={
              <Switch
                checked={mySeriesOnly}
                onChange={e => setMySeriesOnly(e.target.checked)}
                color="primary"
              />
            }
            label="My series only"
          />

          <FormControlLabel
            className={classes.toggleLabel}
            control={
              <Switch
                checked={hideBeforeLastRecorded}
                onChange={e => setHideBeforeLastRecorded(e.target.checked)}
                color="primary"
              />
            }
            label="Hide issues before last owned comic date"
          />
        </div>

        {(selectedPublishers.length > 0 || mySeriesOnly) && (
          <Typography variant="body2" color="textSecondary">
            Showing {filteredSections.reduce((n, section) => n + section.issues.length, 0)} issue(s)
          </Typography>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={classes.root}>
        <Typography variant="h5" className={classes.pageTitle}>New Releases</Typography>
        {filterBar}
        <div className={classes.loadingContainer}>
          <CircularProgress />
          <Typography>Loading new releases…</Typography>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.root}>
        <Typography variant="h5" className={classes.pageTitle}>New Releases</Typography>
        {filterBar}
        <div className={classes.errorContainer}>{error}</div>
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <div className={classes.root}>
        <Typography variant="h5" className={classes.pageTitle}>New Releases</Typography>
        {filterBar}
        <div className={classes.emptyContainer}>
          {queryRequest
            ? <Typography variant="h6">No new releases found for this period.</Typography>
            : <Typography variant="h6">Choose filters, then click Run query.</Typography>}
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Typography variant="h5" className={classes.pageTitle}>New Releases</Typography>

      {/* Filter bar */}
      {filterBar}

      {/* Legend */}
      <div className={classes.legend}>
        <span className={classes.legendItem}>
          <StarIcon style={{ color: '#FFD700', fontSize: 18 }} />
          From a tracked series (not yet owned)
        </span>
        <span className={classes.legendItem}>
          <CheckCircleIcon style={{ color: '#4caf50', fontSize: 18 }} />
          Already owned
        </span>
      </div>

      {/* Selection actions */}
      {selectedIssues.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleAddSelected}
            disabled={adding}
          >
            {adding ? 'Adding…' : `Add ${selectedIssues.size} selected`}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setSelectedIssues(new Set())}
          >
            Clear selection
          </Button>
        </div>
      )}

      {filteredSections.length === 0 ? (
        <div className={classes.emptyContainer}>
          <Typography variant="body1">No releases match the current filters.</Typography>
        </div>
      ) : (
        <div className={classes.issueGrid}>
          {sortedIssues.map(issue => (
            <ReleaseCard
              key={issue.id}
              issue={issue}
              trackedVolumeIds={trackedVolumeIds}
              comicsIGot={comicsIGot}
              classes={classes}
              selected={selectedIssues.has(issue.id)}
              onToggleSelect={() => toggleIssueSelection(issue.id)}
            />
          ))}
        </div>
      )}

      {/* Hero selection dialog for untracked series */}
      <Dialog open={heroDialog.open} onClose={handleHeroDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Select Hero for "{heroDialog.seriesName}"</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            This series is not in your collection. Choose which hero to file it under:
          </Typography>
          <List>
            {(cards || []).map(hero => (
              <ListItem
                button
                key={hero.id || hero.name}
                onClick={() => handleHeroSelected(hero)}
              >
                <ListItemText primary={hero.name} secondary={hero.publisher || ''} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleHeroDialogClose}>Skip</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const ReleaseCard = ({ issue, trackedVolumeIds, comicsIGot, classes, selected, onToggleSelect }) => {
  const volumeId = issue.series && issue.series.id ? Number(issue.series.id) : null;
  const isTracked = volumeId !== null && trackedVolumeIds.has(volumeId);
  const isOwned = comicsIGot && isComicOwned(issue, comicsIGot);

  const imageUrl = hasValidImage(issue)
    ? getImageUrl(issue, 'portrait_medium')
    : null;

  const label = issue.series
    ? `${issue.series.name} #${issue.issueNumber}`
    : issue.title;

  const dateRows = [
    { label: 'Cover', value: issue.coverDate || issue.cover_date },
    { label: 'Added', value: issue.dateAdded || issue.date_added },
    { label: 'Store', value: issue.storeDate || issue.store_date },
  ]
    .map((row) => ({ ...row, value: formatDateValue(row.value) }))
    .filter((row) => row.value);

  return (
    <Tooltip title={label} placement="top">
      <div className={classes.issueWrapper} style={selected ? { outline: '2px solid #1976d2', borderRadius: 4 } : undefined}>
        <Checkbox
          checked={!!selected}
          onChange={onToggleSelect}
          size="small"
          color="primary"
          style={{ position: 'absolute', top: 0, left: 0, zIndex: 2, padding: 2 }}
          onClick={e => e.stopPropagation()}
        />
        <Badge
          badgeContent={getBadgeContent(issue.issueNumber, issue.variantDescription)}
          color="primary"
          max={9999}
        >
          <div className={`${isTracked ? classes.tracked : ''}`}>
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={label}
                className={classes.issueImage}
              />
            ) : (
              <div className={classes.issuePlaceholder}>
                {issue.series ? issue.series.name : issue.title}
              </div>
            )}
          </div>
        </Badge>
        {isTracked && !isOwned && (
          <span className={classes.trackedOverlay}>
            <StarIcon style={{ fontSize: 16 }} />
          </span>
        )}
        {isOwned && (
          <span className={classes.ownedOverlay}>
            <CheckCircleIcon style={{ fontSize: 16 }} />
          </span>
        )}
        <div className={classes.issueTitle}>{label}</div>
        {dateRows.length > 0 && (
          <div className={classes.issueDates}>
            {dateRows.map(row => (
              <div key={row.label}>{row.label}: {row.value}</div>
            ))}
          </div>
        )}
      </div>
    </Tooltip>
  );
};

export default NewReleases;
